import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  FunctionField,
  DateField,
  Button,
  useRefresh,
  useDataProvider,
  useNotify,
  RichTextField,
  ReferenceField,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  TextFieldProps,
  useRecordContext,
  Filter,
  SelectInput,
  TopToolbar,
  usePermissions,
} from 'react-admin';
import { Button as MuiButton } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ListPagination from '../../components/ListPagination';
import { DATE_FORMATER, PERMISSIONS } from '../../constants';
import Permission from '../../components/Permission';
import { SxProps, Theme } from '@mui/material/styles';
import { DispatchMessageOrigin, DispatchMessageStatus } from '../../models';
import { Tooltip } from '../../components';
import RequiredChain from '../../components/RequiredChain';
import DispatchMessageAlertConfigDialog from './DispatchMessageAlertConfigDialog';

const richTextImageSx: SxProps<Theme> = {
  "& img": {
    maxWidth: 300,
    maxHeight: 300
  }
};

const CardinalityField: React.FC<TextFieldProps> = ({ source, textAlign }) => {
  const record = useRecordContext();
  return (
    record && source && record[source] !== null ?
      <NumberField record={record} source={source} />
      :
      <Tooltip
        title="A cardinalidade do envio será definida no momento do envio"
        textAlign={textAlign}
        placement="bottom">A definir</Tooltip>
  );
}

const ListFilter: React.FC = props => (
  <Filter {...props}>
    <SelectInput label="Tipo" source="origin" choices={DispatchMessageOrigin.DispatchMessageOriginChoices} emptyText="Todos" alwaysOn />
  </Filter>
);

/** `admin-profile` é o mesmo gate do backend em `/admin/system-config`. */
const ListActions: React.FC = () => {
  const { permissions } = usePermissions();
  const [configOpen, setConfigOpen] = useState(false);
  const canConfigure = !!permissions && permissions.includes(PERMISSIONS.ADMIN_PROFILE);

  return (
    <TopToolbar>
      {canConfigure && (
        <>
          <MuiButton
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => setConfigOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            Alerta de envio
          </MuiButton>
          <DispatchMessageAlertConfigDialog open={configOpen} onClose={() => setConfigOpen(false)} />
        </>
      )}
    </TopToolbar>
  );
};

const DispatchMessageList: React.FC = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();

  const cancelDispatchMessage = (e: React.MouseEvent, record) => {
    e.stopPropagation();
    setLoading(true);
    dataProvider.delete('chains/chainId/dispatch-messages', { id: record.id, previousData: record })
      .then(() => {
        notify('Envio cancelado com sucesso.');
        refresh();
      })
      .catch(() => { notify('Erro ao cancelar envio.'); })
      .finally(() => { setLoading(false); });
  };

  return (
    <RequiredChain>
      <Permission permission={PERMISSIONS.DISPATCH_MESSAGE}>
        <List
          {...props}
          perPage={25}
          pagination={<ListPagination />}
          exporter={false}
          filters={<ListFilter />}
          actions={<ListActions />}
          bulkActionButtons={false}
          title="Envios"
          sort={{ field: 'sendAt', order: 'DESC' }}
        >
          <Datagrid bulkActionButtons={false} rowClick="show">
            <TextField source="message.title" label="Título" />
            <RichTextField sx={richTextImageSx} source="message.body" label="Mensagem" />
            <DateField source="sendAt" label="Data" options={DATE_FORMATER} />
            <CardinalityField source="cardinality" label="Cardinalidade" textAlign="center" />
            <FunctionField label='Status' render={record => DispatchMessageStatus.translateType(record?.status)} />
            <FunctionField
              label="Grupo de Clientes"
              sortable={false}
              render={(record) => {
                switch (record?.origin) {
                  case DispatchMessageOrigin.unifiedSend:
                    return (
                      <ReferenceField source="messageId" reference="chains/chainId/unified-send" link={false} emptyText="Sem Grupo">
                        <ReferenceField source="customerGroupId" reference="chains/chainId/customer-groups" link={false}>
                          <ChipField source="name" />
                        </ReferenceField>
                      </ReferenceField>
                    )
                  case DispatchMessageOrigin.discountMarketingCampaign:
                    return (
                      <ReferenceField source="messageId" reference="marketing-discounts" link={false} emptyText="Sem Grupo">
                        <ReferenceField source="discountId" reference="discounts" link={false}>
                          <ReferenceArrayField reference="chains/chainId/customer-groups" source="customerGroupIds">
                            <SingleFieldList linkType={false}>
                              <ChipField source="name" />
                            </SingleFieldList>
                          </ReferenceArrayField>
                        </ReferenceField>
                      </ReferenceField>
                    )
                  default:
                    return null;
                }
              }}
            />
            <FunctionField label="Grupo de Postos"
              sortable={false}
              render={(record) => {
                switch (record?.origin) {
                  case DispatchMessageOrigin.unifiedSend:
                    return (
                      <ReferenceField source="messageId" reference="chains/chainId/unified-send" emptyText="Sem Grupo">
                        <ReferenceField source="stationGroupId" reference="chains/chainId/station-groups" link={false}>
                          <TextField source="name" />
                        </ReferenceField>
                      </ReferenceField>
                    );
                  default:
                    return null;
                }
              }}
            />
            <FunctionField label="Categoria de clientes"
              sortable={false}
              render={(record) => {
                switch (record?.origin) {
                  case DispatchMessageOrigin.unifiedSend:
                    return (
                      <ReferenceField source="messageId" reference="chains/chainId/unified-send" emptyText="Sem Grupo">
                        <ReferenceField source="clientCategoryId" reference="client-categories" link={false}>
                          <TextField source="label" />
                        </ReferenceField>
                      </ReferenceField>
                    );
                  default:
                    return null;
                }
              }}
            />
            <FunctionField label="Etiqueta (clientes)"
              sortable={false}
              render={(record) => {
                switch (record?.origin) {
                  case DispatchMessageOrigin.unifiedSend:
                    return (
                      <ReferenceField source="messageId" reference="chains/chainId/unified-send" emptyText="Sem Etiqueta">
                        <ReferenceField source="customerTagId" reference="chains/chainId/tag" link={false}>
                          <TextField source="name" />
                        </ReferenceField>
                      </ReferenceField>
                    );
                  default:
                    return null;
                }
              }}
            />
            <FunctionField label="Etiqueta (postos)"
              sortable={false}
              render={(record) => {
                switch (record?.origin) {
                  case DispatchMessageOrigin.unifiedSend:
                    return (
                      <ReferenceField source="messageId" reference="chains/chainId/unified-send" emptyText="Sem Etiqueta">
                        <ReferenceField source="stationTagId" reference="chains/chainId/tag" link={false}>
                          <TextField source="name" />
                        </ReferenceField>
                      </ReferenceField>
                    );
                  default:
                    return null;
                }
              }}
            />
            <FunctionField source="origin" label="Tipo" render={record => DispatchMessageOrigin.translateOrigin(record ? record.origin : null)} />
            <FunctionField label='' render={record => {
              if (record?.status === 'scheduled') {
                return <Button label='Cancelar Envio' onClick={(e) => cancelDispatchMessage(e, record)} disabled={loading} />;
              }
            }} />
          </Datagrid>
        </List>
      </Permission>
    </RequiredChain>
  );
};

export default DispatchMessageList;
